<template>
  <div class="expendable-faq section">
    <div class="expendable-faq__container">
      <h2
        data-test-id="faq-headline"
        :class="`expendable-faq__headline text-center ${isSmall ? 'text-h3' : ''}`"
        v-html="$t(`pages.faqs.${this.type}.headline`)"
      />
      <ul class="expendable-faq__list">
        <li
          v-for="item in items"
          :key="item.key"
          data-test-id="faq-item"
          :class="{
            'expendable-faq__item': true,
            'expendable-faq__item--active': activeKey === item.key
          }"
          @click="onItemClick(item.key, $event)"
        >
          <div class="expendable-faq__header">
            <h3 :class="`expendable-faq__label ${isSmall ? 'text-h4' : ''}`" v-html="item.headline" />
            <sprite id="app/arrow" class="expendable-faq__sprite" />
          </div>
          <transition-deploy>
            <div class="expendable-faq__text-wrapper" v-if="activeKey === item.key">
              <p class="expendable-faq__text" v-html="item.text" />
            </div>
          </transition-deploy>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import Sprite from "@/shared/sprite"
import TransitionDeploy from "@/shared/transition-deploy"
import globals from "@/utils/globals"
import "/wds/sprites/app/arrow.svg"
import periodCountMixin from "@/mixins/period-count"
import { trackEvent } from "@/services/tracking"

export default {
  name: "ExpendableFaq",
  components: {
    Sprite,
    TransitionDeploy
  },
  mixins: [periodCountMixin],
  props: {
    type: {
      type: String,
      default: "b2c"
    },
    isSmall: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeKey: null
    }
  },
  computed: {
    ...mapGetters("plans", ["trialDays"]),
    items() {
      const items = []
      for (let key of Object.keys(this.$t(`pages.faqs.${this.type}.items`))) {
        if (!this.trialDays && ["howTrialWorks"].includes(key)) {
          continue
        }
        items.push({
          key,
          headline: this.$t(`pages.faqs.${this.type}.items.${key}.headline`, {
            period: this.periodCount(this.trialDays, "hyphen")
          }),
          text: this.$t(`pages.faqs.${this.type}.items.${key}.text`, {
            period: this.periodCount(this.trialDays, "hyphen"),
            totalBooks: globals.totalBooks,
            totalCategories: globals.totalCategories
          }),
          position: this.$t(`pages.faqs.${this.type}.items.${key}.position`) || 0
        })
      }

      items.sort((a, b) => a.position - b.position)
      return items
    }
  },
  watch: {
    trialDays: {
      handler() {
        this.activeKey = this.items[0].key
      },
      immediate: true
    }
  },
  methods: {
    onItemClick(key, e) {
      if (e.target.closest(".expendable-faq__text")) {
        return
      }
      let action
      if (this.activeKey === key) {
        this.activeKey = null
        action = "close"
      } else {
        this.activeKey = key
        action = "open"
      }
      trackEvent(`body-20210218-expendable_faq_${this.type}`, `1-${action}`, key)
    }
  }
}
</script>

<style lang="scss">
.expendable-faq {
  $root: &;

  background-color: $pale-mint-grey;

  &__container {
    @include container;
  }

  &__headline {
    @include font-style(h3);

    margin-bottom: $spacing-24;

    text-align: center;

    @include media(min-width, m) {
      @include font-style(h2);

      margin-bottom: $spacing-64;
    }
  }

  &__item {
    position: relative;

    padding: $spacing-16 0;

    cursor: pointer;

    &:not(:last-child) {
      border-bottom: $midnight solid 1px;
    }

    &--active {
      #{$root} {
        &__sprite {
          transform: scaleY(-1);
        }
      }
    }

    &:last-child {
      padding-bottom: 0;
    }

    @include media(min-width, m) {
      padding: $spacing-32 0;
    }
  }

  &__header {
    display: flex;
    align-items: flex-start;
  }

  &__label {
    @include font-style(h4);

    flex-grow: 1;
    margin: 0;

    @include media(min-width, m) {
      @include font-style(h3);
    }
  }

  &__sprite {
    @include ease(transform, 0.2s);

    flex-shrink: 0;
  }

  &__text {
    @include font-style(p);

    margin: 0;
    padding-top: $spacing-16;

    cursor: auto;

    @include media(min-width, m) {
      padding-top: $spacing-24;
    }
  }

  &__li {
    margin-left: $spacing-32;

    list-style-type: disc;
  }

  mark {
    text-decoration: underline;
  }
}
</style>
